import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faLock, faUser } from '@fortawesome/free-solid-svg-icons';
import { reloginUser } from '../../configurations/features/authSliceUser';

/**
 * Modal de reconnexion affiché après 10 rafraîchissements de token.
 * Se ferme automatiquement si la reconnexion réussit.
 * Contrôlé par showReloginModal dans le state Redux.
 */
const ReloginModal = () => {
    const dispatch = useDispatch();
    const { showReloginModal, refreshCount } = useSelector(state => state.authUser);

    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({ login: false, password: false });

    if (!showReloginModal) return null;

    const handleSubmit = (e) => {
        e.preventDefault();
        const newErrors = {
            login: login.trim().length < 3,
            password: password.trim().length < 3,
        };
        setErrors(newErrors);
        if (newErrors.login || newErrors.password) return;

        setLoading(true);
        // reloginUser : ferme le modal et reste sur la page courante (pas de redirection)
        dispatch(reloginUser(login.trim(), password.trim(), setLoading));
    };

    return (
        <>
            {/* Overlay sombre */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 9998,
                }}
            />

            {/* Modal */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                }}
            >
                <div
                    style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '1rem',
                        boxShadow: '0 25px 60px rgba(0,0,0,0.3)',
                        width: '100%',
                        maxWidth: '420px',
                        padding: '2rem',
                        animation: 'reloginFadeIn 0.3s ease',
                    }}
                >
                    {/* Icône de sécurité */}
                    <div style={{ textAlign: 'center', marginBottom: '1.25rem' }}>
                        <div
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '56px',
                                height: '56px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #7c3aed, #9f67fa)',
                                marginBottom: '0.75rem',
                            }}
                        >
                            <FontAwesomeIcon icon={faLock} style={{ color: '#fff', fontSize: '1.4rem' }} />
                        </div>
                        <h2 style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1f2937', margin: 0 }}>
                            Session expirée
                        </h2>
                        <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.4rem' }}>
                            Pour des raisons de sécurité, veuillez vous reconnecter.
                        </p>
                        {/* Badge compteur */}
                        <div
                            style={{
                                display: 'inline-block',
                                marginTop: '0.5rem',
                                padding: '2px 10px',
                                borderRadius: '999px',
                                backgroundColor: '#f3f0ff',
                                color: '#7c3aed',
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            🔄 {refreshCount} / 10 rafraîchissements effectués
                        </div>
                    </div>

                    {/* Formulaire */}
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                        {/* Login */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                                Login
                            </label>
                            <div style={{ position: 'relative' }}>
                                <FontAwesomeIcon
                                    icon={faUser}
                                    style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                                />
                                <input
                                    id="relogin-login"
                                    type="text"
                                    value={login}
                                    onChange={(e) => setLogin(e.target.value)}
                                    placeholder="Votre identifiant"
                                    style={{
                                        width: '100%',
                                        padding: '10px 12px 10px 36px',
                                        border: `1px solid ${errors.login ? '#ef4444' : '#d1d5db'}`,
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.2s',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                                    onBlur={(e) => e.target.style.borderColor = errors.login ? '#ef4444' : '#d1d5db'}
                                />
                            </div>
                            {errors.login && (
                                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Login requis (min. 3 caractères)
                                </p>
                            )}
                        </div>

                        {/* Mot de passe */}
                        <div>
                            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '4px' }}>
                                Mot de passe
                            </label>
                            <div style={{ position: 'relative' }}>
                                <FontAwesomeIcon
                                    icon={faLock}
                                    style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}
                                />
                                <input
                                    id="relogin-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Votre mot de passe"
                                    style={{
                                        width: '100%',
                                        padding: '10px 40px 10px 36px',
                                        border: `1px solid ${errors.password ? '#ef4444' : '#d1d5db'}`,
                                        borderRadius: '8px',
                                        fontSize: '0.9rem',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        transition: 'border-color 0.2s',
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                                    onBlur={(e) => e.target.style.borderColor = errors.password ? '#ef4444' : '#d1d5db'}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280'
                                    }}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                            {errors.password && (
                                <p style={{ color: '#ef4444', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Mot de passe requis (min. 3 caractères)
                                </p>
                            )}
                        </div>

                        {/* Bouton submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%',
                                padding: '11px',
                                background: loading
                                    ? '#c4b5fd'
                                    : 'linear-gradient(135deg, #7c3aed, #9f67fa)',
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                borderRadius: '8px',
                                border: 'none',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                transition: 'opacity 0.2s',
                                marginTop: '0.25rem',
                            }}
                        >
                            {loading ? 'Connexion en cours…' : 'Se reconnecter'}
                        </button>

                        {/* Lien déconnexion totale */}
                        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#6b7280', margin: 0 }}>
                            Vous souhaitez quitter ?{' '}
                            <span
                                onClick={() => { window.location.href = '/login'; }}
                                style={{ color: '#7c3aed', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}
                            >
                                Se déconnecter
                            </span>
                        </p>
                    </form>
                </div>
            </div>

            {/* Animation CSS */}
            <style>{`
                @keyframes reloginFadeIn {
                    from { opacity: 0; transform: scale(0.95) translateY(-10px); }
                    to   { opacity: 1; transform: scale(1)   translateY(0);     }
                }
            `}</style>
        </>
    );
};

export default ReloginModal;
